/**
 * One decision waiting on the client — `client/requests.html` §2, and the compact
 * form on `client/dashboard.html` §3 and `client/projects.html`.
 *
 * `.docs/19-client-portal-pages.md` §DRY watchlist: "`DecisionCard` is the only
 * rendering of a decision; three pages use it." That is the whole reason it exists,
 * and it is why the **mutations live here** rather than in each page: approving a
 * quote, approving a package change and signing off a content plan are three
 * different routes with one meaning, and a page that wired its own would be a page
 * that could forget the `Idempotency-Key`, the confirm dialog or the impersonation
 * refusal.
 *
 * The four kinds and what each one does, from `OpenDecisionSchema.kind`:
 *
 * | kind | approve | decline | third action |
 * |---|---|---|---|
 * | `quote` | `POST …/requests/:ref/approve` `$` | `…/decline` | Comment |
 * | `extra` | the same two routes | | Comment |
 * | `package_change` | `POST …/retainer/changes/:ref/approve` `$` | `…/decline` | Ask a question |
 * | `content_plan` | `POST …/content-plans/:ref/sign-off` — **no money** | `…/request-changes` | Open the calendar |
 *
 * **Who may answer.** Every decision this system has is `approvers_only`
 * (`OpenDecisionSchema`): the three that cost money go through
 * `security/approvals.ts` and a sign-off through `.docs/00-index.md` §6's own rule.
 * A `member` therefore gets the comment box and **no approve or decline button at
 * all** — the session file's §Edge cases says absent, not disabled, because a
 * disabled button is a promise the account will not keep.
 *
 * **Impersonation is the opposite**, and deliberately: staff viewing a client's
 * portal see the button, disabled, with the reason. `useApiMutation({ money: true })`
 * refuses it before it is sent and `security/impersonation.ts` refuses it again on
 * the server; what the disabled button adds is that the reader is staff and the
 * explanation is for them.
 *
 * ### What the impersonation rule actually is (session 24, R2-31)
 *
 * Not "money approvals are refused" — that is `ARCHITECTURE.md`'s sentence and the
 * code is broader than it, on purpose. Read off the three services that decide:
 *
 * | | who decides | refused while impersonating |
 * |---|---|---|
 * | Approve a quote, an extra, a package change | `assertCanApproveMoney` | **yes** |
 * | **Decline** any of the three | `assertMayDecline` / `retainers.declineChange`, both of which call `assertCanApproveMoney` | **yes** |
 * | Sign off a content plan | `assertCanSignOff` — *no money at all* | **yes** |
 * | Ask for changes to a plan | `requests:write`, and nothing else | **no** |
 * | Comment | `requests:write` | **no** |
 *
 * So the rule is: **an impersonated session may not take a decision that binds the
 * client.** Approving binds them, declining ends the thing irreversibly from the
 * portal, and signing off puts a month of work into production — `.docs/12` asked for
 * that third one by name and said why it is not `money:approve`. Asking for changes
 * commits nothing and releases nothing, and is the one thing staff viewing as a
 * client may still do.
 *
 * Two things were wrong before this session and both are fixed here. **Decline was
 * fully enabled** — a $1,800 quote could be declined while impersonating, dialog and
 * all, and the API refused it with a 403 and an `approval_refused_impersonation`
 * audit row. And the sentence beside the buttons said "Money is approved by the
 * client" on a card whose own answer reads "No money attached", which is the rule
 * stated more narrowly than it is implemented.
 *
 * **The confirm dialog states the amount and the reference** (`.docs/19` §Design
 * decisions), and `ConfirmDialog` is not dismissed by an outside click because it is
 * about money.
 */

import type { ClientDecisionItemView, DecisionKind } from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import {
  useApproveRequest,
  useApproveRetainerChange,
  useCommentOnRequest,
  useDeclineRequest,
  useDeclineRetainerChange,
  useRequestPlanChanges,
  useSignOffContentPlan,
} from '../../api/client/index.js'
import { isImpersonating, usePrincipal, type Principal } from '../../auth/principal.js'
import { abilitiesOf } from '../../pages/client/settings/roles.js'
import { messageOf } from '../../api/problem.js'
import { useFormat } from '../../format/useFormat.js'
import { Button, ButtonRow } from '../portal/Button.js'
import { Card, CardBody, CardHead, CardTitle } from '../portal/Card.js'
import { Dialog, ConfirmDialog } from '../portal/Overlay.js'
import { Stack } from '../portal/Layout.js'
import { NeedsBar, NeedsItem, Notice, Tag } from '../portal/Status.js'
import { B, DateText, HeroCap, Muted, Ref, Small, SubFigure } from '../portal/Text.js'
import { useZodForm, Textarea } from '../portal/Form.js'
import { z } from 'zod'

/** The chip on the card head. `client/requests.html` §2's four `.tag`s. */
export const DECISION_TAG: Readonly<Record<DecisionKind, string>> = {
  quote: 'Project quote',
  extra: 'Extra',
  package_change: 'Retainer',
  content_plan: 'Sign-off',
}

/**
 * The same tag, shorter, where it is prose rather than a chip.
 *
 * `client/requests.html` writes `<span class="tag">Project quote</span>` inside a card
 * head, where it has to be told apart from "Extra"; `client/dashboard.html` writes
 * "Quote · proposed 19 Aug 2026" in a one-line row, where the longer word reads as a
 * category nobody asked about. Partial, over `DECISION_TAG`, so it is one exception
 * rather than a second table that can drift from the first.
 */
const COMPACT_TAG: Partial<Readonly<Record<DecisionKind, string>>> = {
  quote: 'Quote',
}

/** What the approve button says. A sign-off is not an approval. */
export const APPROVE_LABEL: Readonly<Record<DecisionKind, string>> = {
  quote: 'Approve',
  extra: 'Approve',
  package_change: 'Approve',
  content_plan: 'Sign off',
}

/** What refusing is called. "Not now" for a package change — the mockup's word. */
export const DECLINE_LABEL: Readonly<Record<DecisionKind, string>> = {
  quote: 'Decline',
  extra: 'Decline',
  package_change: 'Not now',
  content_plan: 'Request changes',
}

/**
 * What the third button says, **for the two kinds that have somewhere to send it**.
 *
 * A comment is a work-order comment and only a work order has them
 * (`POST /api/clients/me/requests/:ref/comments`). A package change and a content
 * plan have no comment route at all, and the button that used to appear on their
 * cards saying "Ask a question" was wired to `retainers.declineChange` and
 * `retainers.requestChanges` — the same two calls the *Decline* button makes. So a
 * client who asked a question about `RET-CHG-004` declined it, and one who asked
 * about `PLAN-2026-09` moved the plan to `changes_requested`, where the only way
 * out is the studio revising it. Both were irreversible from the portal, and the
 * dialog said "Asking a question does not decline anything" while doing it.
 *
 * A kind with no route gets no button. The capability — a client asking about a
 * package change without answering it — is in the session report as a missing
 * route, not faked with one that answers.
 */
export const TALK_LABEL: Partial<Readonly<Record<DecisionKind, string>>> = {
  quote: 'Comment',
  extra: 'Comment',
}

/** The anchor a decision has on the requests page — `#wo-2026-014`. */
export function decisionAnchor(ref: string): string {
  return ref.toLowerCase()
}

/**
 * What a reader who **can** answer is being asked to do — "Approve Branding — full
 * identity and guidelines", "Sign off Content plan — September 2026".
 *
 * Session 41 (A4). The dashboard's decision rows used to read "Quote · proposed 19
 * Aug 2026" beside a button saying "Review", which names the kind of thing and the
 * day it arrived and never once says what the reader is supposed to do about it. The
 * verb is `APPROVE_LABEL`'s — the same word the card's own primary button carries, so
 * the row and the card cannot promise two different moves — and the object is the
 * decision's own title.
 *
 * It lives here rather than on either page for the reason `APPROVERS_ONLY_NOTE` does:
 * two pages writing the same sentence is two sentences that agree until one is edited.
 */
export function decisionVerb(decision: { readonly kind: DecisionKind; readonly title: string }) {
  return `${APPROVE_LABEL[decision.kind]} ${decision.title}`
}

/** What the row's link says when the reader cannot answer it. */
export const READ_LABEL = 'Read it'

/**
 * The row link's visible words and its accessible name, as one pair.
 *
 * The visible half is short because the row prints the title one line above it and
 * `DESIGN.md` P2 states a fact once — and because the full phrase made the row the
 * widest thing on the page, which scrolled the dashboard sideways at 390.
 *
 * The accessible half is long because four links all called "Approve" is the "Review"
 * problem A4 was written to fix. **It always begins with the visible words**, which
 * is WCAG 2.5.3 *Label in Name*: a speech-input user says what they can see. That is
 * why "Read it" is followed by a dash rather than having the title appended to
 * "Read" — the label has to contain the visible string, not merely the verb.
 */
export function decisionLink(
  decision: { readonly kind: DecisionKind; readonly title: string },
  canAnswer: boolean,
): { readonly text: string; readonly label: string } {
  return canAnswer
    ? { text: APPROVE_LABEL[decision.kind], label: decisionVerb(decision) }
    : { text: READ_LABEL, label: `${READ_LABEL} — ${decision.title}` }
}

/**
 * What a reader who **cannot** answer is told — and, only where it is true, that they
 * may comment.
 *
 * Session 41 (R1-07). The card used to print "Only an owner or an admin can answer
 * this. You can add a comment, and they will see it here." for every kind, in every
 * one of these cases. The second sentence is a promise the card keeps for exactly two
 * of the four kinds: `TALK_LABEL` has `quote` and `extra` and deliberately does not
 * have `package_change` or `content_plan`, because neither has a comment route to
 * send one to (its own docstring says why at length). So a member looking at
 * `RET-CHG-004` was told to add a comment on a card with no comment button anywhere
 * on it.
 *
 * It is **derived from `TALK_LABEL`** rather than written as a second table of the
 * same two kinds. A table listing "these are the kinds that may comment" beside a
 * table listing "these are the kinds with a comment button" is two lists that agree
 * until somebody adds a route to one of them — which is the exact failure this
 * sentence already is.
 */
export function cannotAnswerNote(kind: DecisionKind): string {
  return TALK_LABEL[kind] === undefined
    ? APPROVERS_ONLY_NOTE
    : `${APPROVERS_ONLY_NOTE} You can add a comment, and they will see it here.`
}

/**
 * Whether an impersonated session is refused this move — the table in the header,
 * as a function.
 *
 * `approve` covers signing a content plan off; `decline` covers asking for changes to
 * one. Both are the same button in the same place, and the answer differs by kind
 * because what the button *does* differs by kind: a sign-off releases a month of work
 * and a request for changes releases nothing.
 *
 * It mirrors what the three services already decide rather than adding a fourth rule
 * — `security/approvals.ts` is the one place a money approval is decided and
 * `security/impersonation.ts` is one rule with two callers, and this is the SPA
 * reading them so that no control is offered to somebody the API will refuse.
 */
export function impersonationBlocks(kind: DecisionKind, move: 'approve' | 'decline'): boolean {
  if (kind !== 'content_plan') return true
  // `retainers.signOff` calls `assertCanSignOff`, which refuses an impersonated
  // session; `retainers.requestChanges` checks `requests:write` and nothing else.
  return move === 'approve'
}

/**
 * The sentence beside a disabled control, per kind.
 *
 * Per kind because the reason is: three of these bind money and the fourth binds a
 * month of production, and "Money is approved by the client" printed on a card whose
 * own answer reads "No money attached" is the rule stated more narrowly than it is
 * enforced. Each one ends with what to do instead, because the reader is a studio
 * member and there is something they can do.
 */
export const IMPERSONATION_NOTE: Readonly<Record<DecisionKind, string>> = {
  quote:
    'You are viewing this account as somebody else. Approving and declining a quote are both the client’s to do — ask them, or record their written approval as a studio action.',
  extra:
    'You are viewing this account as somebody else. Approving and declining an extra are both the client’s to do — ask them, or record their written approval as a studio action.',
  package_change:
    'You are viewing this account as somebody else. Answering a package change either way commits them to a monthly figure, so it is theirs to do.',
  content_plan:
    'You are viewing this account as somebody else. Signing off puts a month of work into production on their behalf, which is theirs to do — no money is attached, and that is not why it is refused. Asking for changes commits nothing and is still open to you.',
}

/** True for a role that may answer a decision. Owner and admin, and nobody else. */
export function mayApprove(role: string): boolean {
  return role === 'owner' || role === 'admin'
}

/**
 * Whether **this reader** can answer a decision — the one predicate ochre depends on.
 *
 * `DESIGN.md` P4 is "ochre = this needs **your** decision", so every ochre thing
 * attached to a decision has to ask the same question about the same person. Session
 * 41 answered it in three different places and got three different answers: the
 * compact row asked `mayApprove` alone, the ledger row asked `mayApprove` **and** not
 * impersonating, and the card itself asked nothing at all and was ochre for everybody.
 * So an impersonating staff session read an ochre rail on the dashboard and none on
 * the ledger for the same decision on the same account, and a member read four ochre
 * cards for four decisions they cannot take — which is the defect this session exists
 * to remove, left in the largest place it occurs.
 *
 * Impersonation is in here because the card's own Approve button is disabled under it
 * (`impersonationBlocks`), and `.docs/24` §C4's rule is that a control shown to
 * somebody the API will refuse is worse than no control. Ochre is that control's
 * colour.
 */
export function needsThisReader(principal: Principal): boolean {
  if (principal.kind !== 'client_user') return false
  return mayApprove(principal.role) && !isImpersonating(principal)
}

/**
 * What a page says instead of a call to action, to somebody `mayApprove` refuses.
 *
 * It lives beside the rule and not on either of the two pages that print it. The
 * dashboard wrote this sentence first (`.docs/19` §Edge cases, "buttons absent, not
 * disabled") and the requests hero needed the identical one; a second copy is two
 * sentences that agree until one of them is edited, and this one names the two roles
 * `mayApprove` returns true for — so the day that rule changes, the sentence a
 * `member` reads is one file away from it rather than two pages away.
 *
 * A full stop and no trailing space: both callers append their own clause.
 */
export const APPROVERS_ONLY_NOTE = 'An owner or an admin answers these.'

/**
 * The note a client types when they refuse, ask for changes, or comment.
 *
 * Exported because it is not only this card's: `pages/client/files/MonthFiles.tsx`
 * asks for changes on a deliverable with the same box and the same rule, and
 * `POST …/files/:id/review` accepts an **empty** note for that branch — so the
 * minimum is the UI's promise that the studio gets a sentence rather than the API's.
 * One schema, so the two cannot drift into two different minimums.
 */
export const NoteSchema = z.object({
  note: z.string().trim().min(3, 'A sentence is enough — the studio reads it.').max(2_000),
})

export type DecisionCardProps = {
  readonly decision: ClientDecisionItemView
  /**
   * The detail this kind of decision carries on the requests page — the build-up
   * under a quote, the comparison table under a package change, the credit meter
   * under a plan. The card owns the head, the figure, the actions and nothing in
   * between, because none of that is in the read model.
   */
  readonly children?: ReactNode
  /** The sentence under the figure. The page's answer for this decision. */
  readonly answer?: ReactNode
  /** Called after a successful approve, decline or sign-off. */
  readonly onDecided?: (() => void) | undefined
}

export function DecisionCard({ decision, children, answer, onDecided }: DecisionCardProps) {
  const format = useFormat()
  const principal = usePrincipal()
  const impersonating = isImpersonating(principal)
  const allowed =
    principal.kind === 'client_user' && (!decision.approvers_only || mayApprove(principal.role))

  /*
   * What an impersonated session may not do here. `impersonationBlocks` is the whole
   * table, and its own comment is the argument for each row.
   */
  const blocked = impersonating && impersonationBlocks(decision.kind, 'approve')
  const declineBlocked = impersonating && impersonationBlocks(decision.kind, 'decline')
  const refusalId = `${decisionAnchor(decision.ref)}-impersonation`

  const [confirming, setConfirming] = useState(false)
  const [declining, setDeclining] = useState(false)
  const [talking, setTalking] = useState(false)
  const talkLabel = TALK_LABEL[decision.kind]
  const [failure, setFailure] = useState<string | null>(null)

  const actions = useDecisionActions(decision, {
    onDone: () => {
      setConfirming(false)
      setDeclining(false)
      setTalking(false)
      setFailure(null)
      onDecided?.()
    },
    onTalked: () => {
      setTalking(false)
      setFailure(null)
    },
    onFailure: (message) => {
      setFailure(message)
    },
  })

  const money = decision.amount
  const perMonth = decision.recurrence === 'monthly'

  /*
   * The refusal is rendered wherever the reader is looking.
   *
   * A dialog is modal: Radix marks the rest of the page `aria-hidden`, so a notice
   * on the card behind an open confirm dialog is one nobody — reader or screen
   * reader — can reach. Exactly one of the three dialogs can be open, so this
   * appears once either way, and a note somebody typed is not thrown away by a
   * dialog that closed itself to show an error.
   */
  const anyDialogOpen = confirming || declining || talking
  const failureNotice =
    failure === null ? null : (
      <Notice need role="alert">
        {failure}
      </Notice>
    )

  return (
    /*
     * Ochre only for the reader who can answer it. The card was ochre for everybody
     * until session 41's review found it: a member opened Requests to four ochre
     * cards for four decisions they cannot take, which is the defect this session
     * exists to remove, left in the largest place it occurs. The card keeps its
     * place, its figure and its Comment button either way — what is waiting is still
     * theirs to read.
     */
    <Card need={needsThisReader(principal)} id={decisionAnchor(decision.ref)}>
      <CardHead>
        <CardTitle>{decision.title}</CardTitle>
        <Tag>{DECISION_TAG[decision.kind]}</Tag>
        <Ref value={decision.ref} />
      </CardHead>

      <CardBody>
        <Stack>
          {money === null ? null : (
            <div>
              <HeroCap>{figureCaption(decision, format.date(decision.proposed_at))}</HeroCap>
              <SubFigure value={money} />
              {perMonth ? (
                <Small>
                  <Muted>{recurrenceCaption(decision.kind)}</Muted>
                </Small>
              ) : null}
            </div>
          )}

          {answer}
          {children}

          {anyDialogOpen ? null : failureNotice}

          {impersonating && allowed ? (
            <Notice role="status" id={refusalId}>
              {IMPERSONATION_NOTE[decision.kind]}
            </Notice>
          ) : null}

          <ButtonRow>
            {allowed ? (
              /*
               * `aria-describedby` rather than a `title`.
               *
               * R2-31 found three Approve buttons and a Sign off disabled with no
               * explanation of any kind. A `title` is not the answer — it is not
               * keyboard-reachable and CONTRACT.md's `Common` deliberately admits
               * only ARIA and an id — so the reason is a visible `Notice` above and
               * the button points at it. `aria-disabled` is stated as well as
               * `disabled`, so a reader who lands on the row is told the control is
               * off rather than left to infer it from the styling.
               */
              <Button
                variant="ochre"
                disabled={blocked || actions.busy}
                aria-disabled={blocked ? true : undefined}
                aria-describedby={blocked ? refusalId : undefined}
                onClick={() => {
                  setConfirming(true)
                }}
              >
                {APPROVE_LABEL[decision.kind]}
              </Button>
            ) : null}

            {talkLabel === undefined ? null : (
              <Button
                onClick={() => {
                  setTalking(true)
                }}
              >
                {talkLabel}
              </Button>
            )}

            {allowed ? (
              <Button
                variant="ghost"
                disabled={declineBlocked || actions.busy}
                aria-disabled={declineBlocked ? true : undefined}
                aria-describedby={declineBlocked ? refusalId : undefined}
                onClick={() => {
                  setDeclining(true)
                }}
              >
                {DECLINE_LABEL[decision.kind]}
              </Button>
            ) : null}
          </ButtonRow>

          {allowed ? null : (
            <Small>
              <Muted>{cannotAnswerNote(decision.kind)}</Muted>
            </Small>
          )}
        </Stack>
      </CardBody>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`${APPROVE_LABEL[decision.kind]} ${decision.ref}`}
        confirmLabel={confirmLabel(decision, format.money)}
        busy={actions.busy}
        onConfirm={() => {
          actions.approve()
        }}
      >
        <Small>
          <B>{decision.title}</B>
        </Small>
        <p>{commitmentSentence(decision, format.money)}</p>
        {failureNotice}
      </ConfirmDialog>

      <NoteDialog
        open={declining}
        onOpenChange={setDeclining}
        title={`${DECLINE_LABEL[decision.kind]} — ${decision.ref}`}
        submitLabel={DECLINE_LABEL[decision.kind]}
        busy={actions.busy}
        onSubmit={(note) => {
          actions.decline(note)
        }}
        failure={failureNotice}
      >
        Nothing is charged and nothing was started. Tell us why, in your own words — it goes to the
        studio and stays on the record.
      </NoteDialog>

      {talkLabel === undefined ? null : (
        <NoteDialog
          open={talking}
          onOpenChange={setTalking}
          title={`${talkLabel} — ${decision.ref}`}
          submitLabel="Send"
          busy={actions.busy}
          onSubmit={(note) => {
            actions.comment(note)
          }}
          failure={failureNotice}
        >
          We read it in the portal and answer here. A comment decides nothing — the quote stands
          until you approve or decline it.
        </NoteDialog>
      )}
    </Card>
  )
}

/* ------------------------------------------------------------------- compact */

export type CompactDecisionProps = {
  readonly decision: ClientDecisionItemView
  /**
   * Show the amount. The dashboard does **not** — `client/dashboard.html` §3 is a
   * results page with no money on it, and its own line says "each quote shows its
   * price when you open it".
   */
  readonly showAmount?: boolean
}

/**
 * The one-line form: the dashboard's approvals card and the projects page's
 * "awaiting your approval".
 *
 * It links to the full card rather than duplicating its actions, which is what keeps
 * "three pages, one rendering of a decision" true — a second approve button would be
 * a second confirm dialog and a second place to get the idempotency key wrong.
 *
 * **The link is dropped for a seat that cannot open the page it points at**, and the
 * row stays. `.docs/24` §C4 rule: "a control shown to somebody the API will refuse is
 * worse than no control" — a `billing` seat's `/app` rendered four "Review" links to
 * `/app/requests#…`, all four of which land on `RoleRefusal`, and the panel's own
 * footer already said "An owner or an admin answers these". So the fix is to drop the
 * links, not the panel: what is waiting on the account is a fact a billing reader is
 * entitled to — it decides what the next invoice carries — and `raisesRequests` is
 * the ability that decides whether they can go and read it, the same column
 * `RequestsPage`, `ProjectsPage` and `FilesPage` refuse on.
 *
 * `mayApprove` would be the wrong predicate here: a `member` may not approve and may
 * perfectly well open the request, comment on it and read the price.
 */
export function CompactDecision({ decision, showAmount }: CompactDecisionProps) {
  const format = useFormat()
  const principal = usePrincipal()
  const mayOpen = abilitiesOf(principal).raisesRequests
  /*
   * Session 41 (A4, R1-08, R1-18). The ochre rail means "this needs **your**
   * decision" (`DESIGN.md` P4), and for a member or a billing seat it never did: the
   * row is a fact about the account, not a call to action they can answer. So the
   * rail is drawn for the reader `needsThisReader` names and for nobody else, and the
   * row keeps its place either way — what is waiting is still theirs to know.
   */
  const canAnswer = needsThisReader(principal)
  const link = decisionLink(decision, canAnswer)

  return (
    <NeedsItem>
      {canAnswer ? <NeedsBar /> : null}
      {/*
        Two bare `div`s, because `B` and `Small` are **spans**.
        `mockups/client/dashboard.html` §3 writes `<div class="b">` and
        `<div class="small muted">`, and side by side in one flow the two ran
        together — "Branding — full identity and guidelinesProject quote · proposed
        19 Aug 2026" on every row of the approvals card. A class-less `div` writes no
        CSS, which is why this is the fix rather than a `className` no primitive
        accepts.
      */}
      <div>
        <div>
          <B>{decision.title}</B>
        </div>
        <div>
          <Small>
            <Muted>
              {COMPACT_TAG[decision.kind] ?? DECISION_TAG[decision.kind]} · proposed{' '}
              <DateText value={decision.proposed_at} />
              {showAmount === true && decision.amount !== null ? (
                <>
                  {' '}
                  · {format.money(decision.amount)}
                  {decision.recurrence === 'monthly' ? ' a month' : null}
                </>
              ) : null}
            </Muted>
          </Small>
        </div>
      </div>
      {mayOpen ? (
        <ButtonRow>
          {/*
            The link says what the reader is being asked to do, not "Review" —
            session 41 (A4). A reader who cannot answer gets the same link with the
            neutral word, because opening the decision to read it is a thing they can
            genuinely do and the card at the other end is what tells them who answers.
          */}
          {/* Short on screen, whole to a screen reader — `decisionLink` says why. */}
          <Button size="sm" asChild aria-label={link.label}>
            <Link to="/app/requests" hash={decisionAnchor(decision.ref)}>
              {link.text}
            </Link>
          </Button>
        </ButtonRow>
      ) : null}
    </NeedsItem>
  )
}

/* ------------------------------------------------------------------ the wiring */

type DecisionActions = {
  readonly busy: boolean
  approve: () => void
  decline: (note: string) => void
  comment: (note: string) => void
}

/**
 * The seven mutations behind the three buttons, chosen by kind.
 *
 * All of them are called, and only the right one fires: hooks cannot be called
 * conditionally, and a `useDecisionActions` that branched before its `useMutation`s
 * would break the rules of hooks the moment a card changed kind. What the branch
 * picks is which one to *call*, which is a value.
 */
function useDecisionActions(
  decision: ClientDecisionItemView,
  handlers: {
    /** An approve, a decline or a sign-off landed: the decision is answered. */
    onDone: () => void
    /** A comment landed: the decision is still open, and still the reader's to answer. */
    onTalked: () => void
    onFailure: (message: string) => void
  },
): DecisionActions {
  const approveRequest = useApproveRequest()
  const declineRequest = useDeclineRequest()
  const commentOnRequest = useCommentOnRequest()
  const approveChange = useApproveRetainerChange()
  const declineChange = useDeclineRetainerChange()
  const signOff = useSignOffContentPlan()
  const requestChanges = useRequestPlanChanges()

  const settled = {
    onSuccess: handlers.onDone,
    onError: (error: Error) => {
      /*
       * `messageOf`, not `error.message`. `ApiProblem` extends `Error`, so the
       * second compiles and gives the generic *title* — "Forbidden" — where
       * `detail` is the sentence the API wrote for this reader ("Only an owner or
       * an admin can approve money"). Six notices across four pages made this
       * choice separately and three made it the other way.
       */
      handlers.onFailure(messageOf(error))
    },
  }

  const busy =
    approveRequest.isPending ||
    declineRequest.isPending ||
    commentOnRequest.isPending ||
    approveChange.isPending ||
    declineChange.isPending ||
    signOff.isPending ||
    requestChanges.isPending

  const { kind, ref } = decision

  return {
    busy,
    approve: () => {
      if (kind === 'package_change') approveChange.mutate(ref, settled)
      else if (kind === 'content_plan') signOff.mutate(ref, settled)
      else approveRequest.mutate(ref, settled)
    },
    decline: (note) => {
      if (kind === 'package_change') declineChange.mutate({ ref, note }, settled)
      else if (kind === 'content_plan') requestChanges.mutate({ ref, note }, settled)
      else declineRequest.mutate({ ref, note }, settled)
    },
    /*
     * Only a work order has comments, and `TALK_LABEL` is what keeps the button off
     * the two cards that do not — so this is reached for a quote and an extra and
     * nothing else. It used to fall through to `declineChange` / `requestChanges`
     * for the other two, which answered a decision the reader was asking about.
     */
    comment: (body) => {
      commentOnRequest.mutate({ ref, body }, { ...settled, onSuccess: handlers.onTalked })
    },
  }
}

/* ---------------------------------------------------------------- the wording */

type MoneyFormatter = ReturnType<typeof useFormat>['money']

/**
 * The caption over the figure. Each kind names what the amount is.
 *
 * **An extra has two captions, because it has two shapes.** `recurrence: monthly`
 * (`.docs/00-index.md` §6, recurring extras) means the amount is charged every period
 * until the client cancels it, and "Total, on your next monthly invoice" printed over
 * one said the opposite of the line underneath it, of the ledger row for the same
 * request on the same page ("$600.00 **per month**") and of the approval dialog
 * `confirmLabel` writes ("Approve $600.00 a month"). Three of the four surfaces knew;
 * this was the fourth.
 */
function figureCaption(decision: ClientDecisionItemView, proposed: string): string {
  switch (decision.kind) {
    case 'quote':
      return 'Fixed total'
    case 'extra':
      return decision.recurrence === 'monthly'
        ? 'Per month, on every monthly invoice'
        : 'Total, on your next monthly invoice'
    case 'package_change':
      return 'Change to your monthly amount'
    case 'content_plan':
      return `Proposed ${proposed}`
  }
}

/**
 * The line under a recurring figure — what "every month" means for **this** kind.
 *
 * A package change moves the retainer's own monthly amount, so it takes effect on the
 * next billing day and runs until the package changes again. A recurring extra is a
 * line added to every monthly invoice and is ended by cancelling the extra
 * (`commitmentSentence`: "…and on every monthly invoice after it until you cancel
 * it"). One sentence for both said the package change's thing about the extra.
 */
export function recurrenceCaption(kind: DecisionKind): string {
  return kind === 'extra'
    ? 'every month, on every monthly invoice, until you cancel it'
    : 'every month, from the day it takes effect'
}

/** What the confirm button says. It states the amount and the reference (§Design). */
export function confirmLabel(decision: ClientDecisionItemView, money: MoneyFormatter): string {
  if (decision.amount === null) return `${APPROVE_LABEL[decision.kind]} ${decision.ref}`
  const amount = money(decision.amount)
  return decision.recurrence === 'monthly'
    ? `Approve ${amount} a month · ${decision.ref}`
    : `Approve ${amount} · ${decision.ref}`
}

/** What approving commits to, in one sentence — `client/requests.html`'s own fold. */
export function commitmentSentence(
  decision: ClientDecisionItemView,
  money: MoneyFormatter,
): string {
  const amount = decision.amount === null ? null : money(decision.amount)
  switch (decision.kind) {
    case 'quote':
      return `The deposit invoice is issued immediately and the remaining milestones invoice on delivery. ${
        amount === null ? '' : `The total is ${amount}.`
      }`.trim()
    /*
     * Session 24 (R3-26). This said "lands as a line on your next monthly invoice, in
     * arrears" and stopped there, while `/app/billing`'s estimate — which the client
     * reads straight afterwards — did not move at all. The two read as a contradiction
     * and neither was wrong: `SERVICES.md` §Extras is "in arrears, **after delivery**",
     * and `billing/queries.ts` `billableExtras` is that rule in SQL — a one-off extra
     * joins an invoice at `delivered`, not at `approved`. The missing word was *when*.
     *
     * A **recurring** extra is the exception and is the one case this dialog already
     * knew about (`confirmLabel`'s "a month"): `billableExtras` takes it at
     * `approved | in_progress | delivered`, so it is on the estimate from the day it is
     * agreed and on every invoice after that until it is cancelled.
     */
    case 'extra':
      return decision.recurrence === 'monthly'
        ? `Work starts, and ${
            amount ?? 'the amount'
          } lands as a line on your next monthly invoice, in arrears, with the wording you are approving — and on every monthly invoice after it until you cancel it.`
        : `Work starts, and ${
            amount ?? 'the amount'
          } lands as a line on your next monthly invoice, in arrears, with the wording you are approving: the first one issued after we deliver it, so your billing estimate does not move today.`
    case 'package_change':
      return `It takes effect on your next billing day, never mid-period${
        amount === null ? '' : `, and changes your monthly amount by ${amount}`
      }.`
    case 'content_plan':
      return 'No money. Signing off releases this month of work into production.'
  }
}

/* ------------------------------------------------------------- the note dialog */

export type NoteDialogProps = {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly title: string
  readonly submitLabel: string
  readonly busy: boolean
  readonly onSubmit: (note: string) => void
  readonly failure?: ReactNode
  /** What the box is for, above it. One muted sentence. */
  readonly children: ReactNode
}

/**
 * "Tell us why", in a dialog. Exported for the same reason `NoteSchema` is.
 *
 * It is not money — no `ConfirmDialog`, so an outside click dismisses it — and the
 * note is the whole payload. `MonthFiles` uses it for *Request changes* on a
 * deliverable, which is the same interaction as declining a quote: a sentence, a
 * submit, and the refusal rendered where the reader is looking.
 */
export function NoteDialog({
  open,
  onOpenChange,
  title,
  submitLabel,
  busy,
  onSubmit,
  failure,
  children,
}: NoteDialogProps) {
  const form = useZodForm(NoteSchema, { defaultValues: { note: '' } })

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title}>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void form.handleSubmit((values) => {
            onSubmit(values.note)
          })(event)
        }}
      >
        <Stack>
          <Small>
            <Muted>{children}</Muted>
          </Small>
          <Textarea control={form.control} name="note" label="Your note" />
          {failure}
          <ButtonRow>
            <Button type="submit" variant="primary" disabled={busy}>
              {submitLabel}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Cancel
            </Button>
          </ButtonRow>
        </Stack>
      </form>
    </Dialog>
  )
}
